/**
 * Projects — Stride's tenancy boundary.
 *
 * A project is "your own Stride instance": connections, pipelines, sets, Jira config, promotions and
 * runs all belong to exactly one, and a person who is not a member cannot see any of it.
 *
 * ── Two levels of authority, deliberately separate ──
 * "Admin of my project" and "admin of Stride" are different powers, and collapsing them is how a
 * DevOps user ends up able to read every other team's credentials:
 *
 *  · PLATFORM admin — named in `STRIDE_BOOTSTRAP_ADMINS`. Sees and acts in every project. The
 *    install's owner.
 *  · PROJECT admin  — a role row inside one project. Manages that project's members, connections and
 *    pipeline, and has no visibility into any other project at all.
 *
 * Platform-admin access to someone else's project is surfaced in the UI and stamped on audit records
 * rather than being silent. Support access indistinguishable from ownership is how audit logs stop
 * being believed.
 */
export const ENV_CLASSES = ['development', 'testing', 'staging', 'production'];
/** Ordered least → most sensitive, so a policy can say "staging and above". */
export const ENV_CLASS_RANK = {
    development: 0, testing: 1, staging: 2, production: 3,
};
/**
 * Slugify a project name into an id.
 *
 * Exported and pure because the UI previews the id while the user types, and the two must agree —
 * a preview that disagrees with what the server stores is a bug report about "the name I picked
 * disappeared".
 */
export function projectSlug(name) {
    return name
        .toLowerCase()
        // Strip accents rather than dropping the letter: "Café" → "cafe", not "caf".
        .normalize('NFKD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 48);
}
/** Ids that would collide with existing storage partitions or route segments. */
export const RESERVED_PROJECT_IDS = ['default', 'new', 'api', 'admin', 'me', 'settings'];
/**
 * Why a project id is unacceptable, or null when it is fine.
 *
 * Returns the reason rather than a boolean so the UI can say what to change. A validator that only
 * says "invalid" makes the user guess.
 */
export function projectIdProblem(id) {
    if (id.length === 0)
        return 'Enter a project name.';
    if (id.length > 48)
        return 'That name is too long — 48 characters or fewer once converted.';
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(id)) {
        return 'Use letters, numbers and hyphens; start and end with a letter or number.';
    }
    if (RESERVED_PROJECT_IDS.includes(id)) {
        return `"${id}" is reserved — pick another name.`;
    }
    return null;
}
//# sourceMappingURL=project.js.map